import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconArrowLeft,
  IconBuildingSkyscraper,
  IconCalendar,
  IconChartBar,
  IconCircleFilled,
  IconClock,
  IconCloud,
  IconFile,
  IconFileSpreadsheet,
  IconSend,
  IconUsers,
  IconPhoneCall
} from '@tabler/icons-react';
import { Button } from '@core-ui/Components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@core-ui/Components/ui/avatar';
import { Badge } from '@core-ui/Components/ui/badge';
import ReportDownloadModal, { ExportButton, type ReportType } from './ReportDownloadModal';
import { type ContactListType, type SessionDetailsInfo, type SessionStats, SessionStatus } from '../types/dashboard.types';
import { Check, Copy } from 'lucide-react';

interface SessionHeaderProps {
  session: SessionDetailsInfo;
  stats: SessionStats;
  listName: string;
  /** Distinct contacts this session dialled. */
  contactsCalled: number | null;
  /** How many contacts the session's list holds. */
  listContacts: number | null;
  startDate: string;
  onBack: () => void;
  onDownloadReport: (type: ReportType) => Promise<void>;
  isDownloading?: boolean;
}

function getInitials (name: string): string {
  if (name === '') {
    return '??';
  }
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

function formatDate (dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function calculateDuration (startAtSession: string, updatedAtSession: string): string {
  const start = new Date(startAtSession);
  const end = new Date(updatedAtSession);
  const diffMs = end.getTime() - start.getTime();

  if (diffMs < 0) {
    return '0m';
  }

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

function getStatusConfig (status: SessionStatus, t: (key: string) => string): { label: string; className: string } {
  switch (status) {
  case SessionStatus.FINISHED:
    return {
      label: t('dashboard.sessionHeader.completed'),
      className: 'bg-green-500/20 text-green-400 border-green-500/30',
    };
  case SessionStatus.PAUSED:
    return {
      label: t('dashboard.sessionHeader.paused'),
      className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    };
  case SessionStatus.STARTED:
  default:
    return {
      label: t('dashboard.sessionHeader.inProgress'),
      className: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    };
  }
}

function getListTypeLabel (type: ContactListType): string {
  const labels: Record<ContactListType, string> = {
    CSV: 'CSV',
    HUBSPOT: 'HubSpot',
    CUSTOM_HUBSPOT: 'HubSpot',
    OUTREACH: 'Outreach',
    SALESFORCE: 'Salesforce',
    FOURMATIV: 'Fourmativ',
  };
  return labels[type] !== '' ? labels[type] : type;
}

function getListTypeIcon (type: ContactListType): React.ReactElement {
  const iconClass = 'h-4 w-4';

  switch (type) {
  case 'CSV':
    return <IconFileSpreadsheet className={iconClass} />;
  case 'HUBSPOT':
  case 'CUSTOM_HUBSPOT':
    return <IconCircleFilled className={`${iconClass} text-orange-500`} />;
  case 'SALESFORCE':
    return <IconCloud className={`${iconClass} text-blue-400`} />;
  case 'OUTREACH':
    return <IconSend className={`${iconClass} text-purple-400`} />;
  case 'FOURMATIV':
    return <IconChartBar className={iconClass} />;
  default:
    return <IconFile className={iconClass} />;
  }
}

export default function SessionHeader ({
  session,
  stats,
  listName,
  contactsCalled,
  listContacts,
  startDate,
  onBack,
  onDownloadReport,
  isDownloading = false,
}: SessionHeaderProps): React.ReactElement {
  const { t } = useTranslation();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const statusConfig = getStatusConfig(session.status, t);
  const duration = calculateDuration(session.startAtSession, session.updatedAtSession);

  const handleCopy = async (id: string): Promise<void> => {
    await navigator.clipboard.writeText(id);
    setCopiedId(id);

    setTimeout(() => {
      setCopiedId(null);
    }, 1500);
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-4">
          <Button
            data-qa-id="back-button"
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="h-9 w-9 mt-1"
          >
            <IconArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 data-qa-id="list-name" className="text-2xl font-bold text-foreground">{listName}</h1>
              <Badge
                variant="outline"
                className={`text-xs px-2.5 py-0.5 ${statusConfig.className}`}
              >
                {statusConfig.label}
              </Badge>
            </div>

            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1.5">
                {getListTypeIcon(session.listType)}
                {getListTypeLabel(session.listType)}
              </span>
              {/*
                Two separate figures. The header used to show one number taken
                from the contact list and labelled "contacts", which read as if
                the session had called all of them — 112 for a session that
                dialled 36.
              */}
              <span className="flex items-center gap-1.5">
                <IconPhoneCall className="h-4 w-4" />
                {contactsCalled ?? '—'} {t('dashboard.sessionHeader.contactsCalled', 'contacts called')}
              </span>
              <span className="flex items-center gap-1.5">
                <IconUsers className="h-4 w-4" />
                {listContacts ?? '—'} {t('dashboard.sessionHeader.listContacts', 'in list')}
              </span>
              <span className="flex items-center gap-1.5">
                <IconBuildingSkyscraper className="h-4 w-4" />
                {session.organizationName}
              </span>
              <span className="flex items-center gap-1.5">
                <IconCalendar className="h-4 w-4" />
                {formatDate(startDate)}
              </span>
              <span className="flex items-center gap-1.5">
                <IconClock className="h-4 w-4" />
                {duration} {t('dashboard.sessionHeader.session')}
              </span>
            </div>
          </div>
        </div>
        <div>
          <div className="flex justify-end">
            <ExportButton
              onClick={() => {
                setIsReportModalOpen(true); 
              }}
              isDownloading={isDownloading}
            />
          </div>
          <div className='w-full mt-2 flex items-center justify-end'>
            <span className="text-xs text-muted-foreground break-words">{t('dashboard.sessionHeader.sessionID', 'Session ID')}: </span>
            <span className="text-xs break-words font-mono">{session.callSessionId}</span>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={() => {
                void handleCopy(session.callSessionId);
              }}
              aria-label={t('adminConsole.activeCallSessions.copySessionId', 'Copy session ID')}
              title={t('adminConsole.activeCallSessions.copySessionId', 'Copy session ID')}
            >
              {copiedId === session.callSessionId
                ? <Check className="size-3" />
                : <Copy className="size-3" />
              }
            </Button>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-8 mt-6">
        <Avatar className="h-14 w-14 shadow-lg">
          {session.sdrProfilePicture != null && session.sdrProfilePicture !== '' && (
            <AvatarImage
              src={session.sdrProfilePicture}
              alt={session.sdrName}
            />
          )}
          <AvatarFallback className="bg-primary/20 text-primary text-lg font-semibold">
            {getInitials(session.sdrName)}
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="font-semibold text-foreground text-lg">{session.sdrName}</p>
          <p className="text-sm text-muted-foreground">
            {t('dashboard.sessionHeader.sdr')} {session.sdrEmail != null && session.sdrEmail !== '' ? `• ${session.sdrEmail}` : ''}
          </p>
        </div>

        <div className="h-10 w-px bg-border" />

        <div className="text-center">
          <p className="text-3xl font-bold text-foreground">{stats.totalCalls.toLocaleString()}</p>
          <p className="text-sm text-muted-foreground">{t('dashboard.sessionHeader.totalCalls')}</p>
        </div>
        <div className="text-center">
          <p className="text-3xl font-bold text-green-500">{stats.dialToConnectRatio.toFixed(1)}%</p>
          <p className="text-sm text-muted-foreground">{t('dashboard.sessionHeader.connectRate')}</p>
        </div>
        <div className="text-center">
          <p className="text-3xl font-bold text-purple-500">{stats.setAppointments}</p>
          <p className="text-sm text-muted-foreground">{t('dashboard.sessionHeader.appointments')}</p>
        </div>
      </div>

      <ReportDownloadModal
        open={isReportModalOpen}
        onOpenChange={setIsReportModalOpen}
        listType={session.listType}
        onDownload={onDownloadReport}
      />
    </div>
  );
}
